import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Loader2 } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CMSPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadPage = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_BASE}/pages/${slug}`);
      setPage(response.data);
    } catch (err) {
      console.error('Error loading page:', err);
      if (err.response?.status === 404) {
        setError('Page non trouvée');
      } else {
        setError('Erreur lors du chargement de la page');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--sage)]" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex flex-col items-center justify-center py-32">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-xl text-muted-foreground mb-8">{error || 'Page non trouvée'}</p>
          <Button onClick={() => navigate('/')}>
            Retour à l'accueil
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Convert form questions to the format expected by ServiceLeadForm
  const formQuestions = page.form_questions?.map(q => ({
    id: q.id || q.label?.toLowerCase().replace(/\s+/g, '_'),
    label: q.label,
    type: q.type,
    placeholder: q.placeholder || '',
    options: q.options || [],
  })) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="cms-page">
      <SEOHead 
        title={`${page.meta_title} | Homely Solutions`}
        description={page.meta_description}
        canonical={`https://www.homely-solutions.ch/p/${page.slug}`}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section 
        className="hero-section relative min-h-[70vh] flex items-center"
        style={{ backgroundColor: page.hero_image_url ? 'transparent' : 'var(--sage)' }}
      >
        {page.hero_image_url && (
          <img 
            src={page.hero_image_url} 
            alt={page.hero_title} 
            className="hero-image-bg"
          />
        )}
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {page.parent_service && (
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                {page.parent_service.charAt(0).toUpperCase() + page.parent_service.slice(1)}
              </Badge>
            )}
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              {page.hero_title}
            </h1>
            
            {page.hero_subtitle && (
              <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl">
                {page.hero_subtitle}
              </p>
            )}
            
            {page.hero_cta_text && (
              <Button 
                size="lg" 
                className="btn-premium h-14 px-10 rounded-full"
                onClick={() => {
                  if (page.hero_cta_link === '#form') {
                    scrollToForm();
                  } else {
                    navigate(page.hero_cta_link);
                  }
                }}
                data-testid="hero-cta-btn"
              >
                {page.hero_cta_text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Dynamic Sections */}
      {page.sections?.map((section, index) => (
        <section 
          key={section.id || index}
          className="py-16"
          style={{ backgroundColor: section.background_color || (index % 2 === 1 ? '#f9fafb' : 'white') }}
        >
          <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
            {/* Text Section */}
            {section.type === 'text' && (
              <div className="max-w-3xl mx-auto">
                {section.title && (
                  <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                    {section.title}
                  </h2>
                )}
                {section.subtitle && (
                  <p className="text-lg text-muted-foreground mb-6 text-center">
                    {section.subtitle}
                  </p>
                )}
                {section.content && (
                  <div className="prose prose-lg max-w-none">
                    {section.content.split('\n').map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CTA Section */}
            {section.type === 'cta' && (
              <div className="text-center py-8">
                {section.title && (
                  <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    {section.title}
                  </h2>
                )}
                {section.subtitle && (
                  <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                    {section.subtitle}
                  </p>
                )}
                {section.cta_text && (
                  <Button 
                    size="lg" 
                    className="h-14 px-10 rounded-full"
                    style={{ backgroundColor: 'var(--sage)' }}
                    onClick={() => {
                      if (section.cta_link === '#form') {
                        scrollToForm();
                      } else {
                        navigate(section.cta_link);
                      }
                    }}
                  >
                    {section.cta_text}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                )}
              </div>
            )}

            {/* Image Section */}
            {section.type === 'image' && section.image_url && (
              <div className="max-w-4xl mx-auto">
                {section.title && (
                  <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                    {section.title}
                  </h2>
                )}
                <img 
                  src={section.image_url} 
                  alt={section.title || 'Image'} 
                  className="w-full rounded-2xl shadow-lg"
                />
                {section.content && (
                  <p className="text-center text-muted-foreground mt-4">
                    {section.content}
                  </p>
                )}
              </div>
            )}

            {/* Features Section */}
            {section.type === 'features' && (
              <div>
                {section.title && (
                  <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                    {section.title}
                  </h2>
                )}
                {section.content && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {section.content.split('\n').filter(Boolean).map((feature, i) => (
                      <div key={i} className="bg-white p-6 rounded-xl shadow-sm border">
                        <p className="font-medium">{feature}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Testimonials Section */}
            {section.type === 'testimonials' && (
              <div>
                {section.title && (
                  <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                    {section.title}
                  </h2>
                )}
                {section.content && (
                  <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border">
                    <p className="text-lg italic text-muted-foreground mb-4">
                      "{section.content}"
                    </p>
                    {section.subtitle && (
                      <p className="font-medium text-right">— {section.subtitle}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Form Section */}
      {page.show_form && (
        <section 
          className="py-20" 
          style={{ backgroundColor: 'var(--sage)' }} 
          ref={formRef}
          id="form"
        >
          <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="text-white">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  {page.form_title || 'Demandez votre devis gratuit'}
                </h2>
                <p className="text-white/80 mb-8">
                  Remplissez le formulaire et nous vous recontacterons dans les plus brefs délais.
                </p>
                
                <div className="space-y-3">
                  {['Réponse sous 24h', 'Devis gratuit et sans engagement', 'Conseils personnalisés'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-white/90">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
                <ServiceLeadForm 
                  service={page.slug} 
                  pole={page.parent_service || 'cms'} 
                  questions={formQuestions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};
